import React, { useState, useEffect, useCallback, useContext } from 'react';
import { conversationAPI } from '../api/conversationAPI.js';
import { AuthContext } from '../App.js';
import '../dashboard/Dashboard.css';

// CSS for loading skeleton animation
const skeletonStyle = document.createElement('style');
skeletonStyle.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;
document.head.appendChild(skeletonStyle);

const ConversationManager = ({ isDarkMode, styles, isMobile, wait }) => {
  const { vectorVault } = useContext(AuthContext);
  // State for sessions/conversations
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // State for pagination and filtering
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10); // Smaller initial load for faster response
  const [totalSessions, setTotalSessions] = useState(0);
  
  // State for filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For the input field before search is executed
  const [searchType, setSearchType] = useState('all'); // 'all', 'messages', 'sessions'
  const [isSearchMode, setIsSearchMode] = useState(false); // Track if we're in search mode
  const [selectedFlow, setSelectedFlow] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [sortBy, setSortBy] = useState('last_message_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  
  // State for selected session and messages
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionMessages, setSessionMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [expandedMetadata, setExpandedMetadata] = useState(new Set());
  const [messagesCache, setMessagesCache] = useState(new Map()); // Cache messages to avoid re-fetching
  
  // State for stats
  const [stats, setStats] = useState(null);
  
  // State for available filters
  const [availableFlows, setAvailableFlows] = useState([]);
  const [availableCustomers, setAvailableCustomers] = useState([]);

  // Fetch sessions
  const fetchSessions = async () => {
    setLoading(true);
    setError('');
    
    try {
      let data;
      
      // If there's a search query, use the new search functionality
      if (searchQuery.trim()) {
        setIsSearchMode(true);
        const searchParams = {
          page: currentPage,
          page_size: pageSize,
          ...(selectedFlow && { flow_name: selectedFlow })
        };
        
        if (searchType === 'messages') {
          const result = await conversationAPI.searchMessages(vectorVault, searchQuery, searchParams);
          console.log('Message search result:', result); // Debug log
          
          // Handle the correct response structure: result.results.messages.messages
          const messages = result?.results?.messages?.messages || result?.messages || [];
          console.log('Extracted messages:', messages); // Debug log
          
          // Transform message search results to session format
          if (messages.length > 0) {
            // Group messages by session and create session-like objects
            const sessionMap = new Map();
            messages.forEach(msg => {
              if (!sessionMap.has(msg.session_id)) {
                sessionMap.set(msg.session_id, {
                  session_id: msg.session_id,
                  customer_id: msg.customer_id,
                  customer_email: msg.customer_email || msg.session_name,
                  customer_name: msg.customer_name,
                  session_name: msg.session_name,
                  flow_name: msg.flow_name,
                  created_at: msg.session_created_at || msg.created_at,
                  last_message_at: msg.timestamp,
                  message_count: msg.session_message_count || 1,
                  matching_messages: [msg]
                });
              } else {
                const session = sessionMap.get(msg.session_id);
                session.matching_messages.push(msg);
                // Update last message time if this message is newer
                if (new Date(msg.timestamp) > new Date(session.last_message_at)) {
                  session.last_message_at = msg.timestamp;
                }
              }
            });
            
            data = {
              sessions: Array.from(sessionMap.values()),
              count: result?.results?.messages?.total_count || result?.total_count || sessionMap.size
            };
          } else {
            data = { sessions: [], count: 0 };
          }
        } else {
          // For session search or unified search
          const result = await conversationAPI.unifiedSearch(vectorVault, searchQuery, {
            ...searchParams,
            type: searchType
          });
          console.log('Unified search result:', result); // Debug log
          
          // Handle the correct response structure: result.results.sessions.sessions
          const sessions = result?.results?.sessions?.sessions || result?.sessions || [];
          console.log('Extracted sessions:', sessions); // Debug log
          
          data = {
            sessions: sessions,
            count: result?.results?.sessions?.total_count || result?.count || result?.total_count || sessions.length
          };
        }
      } else {
        // Regular session fetch without search
        setIsSearchMode(false);
        const params = {
          page: currentPage,
          page_size: pageSize,
          sort_by: sortBy,
          sort_order: sortOrder,
          ...(selectedFlow && { flow_name: selectedFlow }),
          ...(selectedCustomer && { customer_id: selectedCustomer })
        };
        data = await conversationAPI.getSessions(vectorVault, params);
      }
      
      console.log('Final data to set:', data); // Debug log
      setSessions(data.sessions || []);
      console.log('Sessions set to:', data.sessions || []); // Debug log
      setTotalSessions(data.count || 0);
      
      // Extract unique flows and customers for filter options
      const flows = [...new Set(data.sessions?.map(s => s.flow_name).filter(Boolean))];
      const customers = [...new Set(data.sessions?.map(s => ({
        id: s.customer_id,
        email: s.customer_email,
        name: s.customer_name
      })).filter(c => c.id))];
      
      setAvailableFlows(flows);
      setAvailableCustomers(customers);
      
    } catch (err) {
      setError(`Failed to fetch conversations: ${err.message}`);
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const statsData = await conversationAPI.getStats(vectorVault);
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Fetch session messages with caching
  const fetchSessionMessages = async (sessionId) => {
    if (!vectorVault || wait) return;
    
    // Check cache first
    if (messagesCache.has(sessionId)) {
      setSessionMessages(messagesCache.get(sessionId));
      return;
    }
    
    setMessagesLoading(true);
    try {
      const data = await conversationAPI.getSessionMessages(vectorVault, sessionId, {
        page_size: 100, // Get more messages for viewing
        sort_by: 'timestamp',
        sort_order: 'ASC' // Oldest messages first
      });
      
      // Use backend sorting if available, otherwise fallback to frontend sorting
      let messages = data.messages || [];
      if (messages.length > 1) {
        // Quick check if backend sorting worked
        const firstTime = new Date(messages[0]?.timestamp || 0).getTime();
        const lastTime = new Date(messages[messages.length - 1]?.timestamp || 0).getTime();
        
        // If not properly sorted (first > last), sort on frontend
        if (firstTime > lastTime) {
          messages = messages.sort((a, b) => {
            const timestampA = new Date(a.timestamp || 0).getTime();
            const timestampB = new Date(b.timestamp || 0).getTime();
            return timestampA - timestampB;
          });
        }
      }
      
      // Cache the messages
      setMessagesCache(prev => new Map(prev.set(sessionId, messages)));
      setSessionMessages(messages);
    } catch (err) {
      setError(`Failed to fetch messages: ${err.message}`);
      console.error('Error fetching session messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  // Handle session selection
  const handleSessionSelect = async (session) => {
    setSelectedSession(session);
    setShowMessageModal(true);
    setExpandedMetadata(new Set()); // Reset expanded metadata when opening new session
    await fetchSessionMessages(session.session_id);
  };

  // Helper function to highlight search terms in text
  const highlightSearchTerms = (text, searchQuery) => {
    if (!searchQuery || !isSearchMode || !text) return text;
    
    const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{ 
          backgroundColor: isDarkMode ? '#00d67a' : '#00d67a',
          color: isDarkMode ? '#000' : '#000',
          padding: '1px 2px',
          borderRadius: '2px'
        }}>
          {part}
        </mark>
      ) : part
    );
  };

  // Toggle metadata visibility for a specific message
  const toggleMetadata = (messageId) => {
    const newExpanded = new Set(expandedMetadata);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedMetadata(newExpanded);
  };

  // Handle session deletion
  const handleDeleteSession = async (session) => {
    if (!vectorVault) return;
    if (!window.confirm(`Are you sure you want to delete this conversation? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await conversationAPI.deleteSession(vectorVault, session.session_id, session.customer_id);
      
      // Clear cached messages for deleted session
      setMessagesCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(session.session_id);
        return newCache;
      });
      
      fetchSessions(); // Refresh the list
      setShowMessageModal(false);
    } catch (err) {
      setError(`Failed to delete session: ${err.message}`);
    }
  };

  // Handle sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'DESC' ? 'ASC' : 'DESC');
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
    setCurrentPage(0);
  };

  // Handle search input change (doesn't trigger search)
  const handleSearchInput = (e) => {
    setSearchInput(e.target.value);
  };

  // Perform the actual search
  const performSearch = () => {
    setSearchQuery(searchInput.trim());
    setCurrentPage(0);
  };

  // Handle Enter key in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      performSearch();
    }
  };

  // Handle search type change
  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    setCurrentPage(0);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchInput('');
    setIsSearchMode(false);
    setCurrentPage(0);
  };

  // Handle filter changes
  const handleFlowFilter = (e) => {
    setSelectedFlow(e.target.value);
    setCurrentPage(0);
  };

  const handleCustomerFilter = (e) => {
    setSelectedCustomer(e.target.value);
    setCurrentPage(0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Format message timestamp
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const maxPage = Math.ceil(totalSessions / pageSize) - 1;
    if (currentPage < maxPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Effect to fetch data in parallel
  useEffect(() => {
    if (!vectorVault || wait) return;
    
    // Fetch both sessions and stats in parallel for faster loading
    Promise.all([
      fetchSessions(),
      fetchStats()
    ]).catch(err => {
      console.error('Error fetching conversation data:', err);
    });
  }, [vectorVault, wait, currentPage, pageSize, searchQuery, searchType, sortBy, sortOrder, selectedFlow, selectedCustomer]);

  return (
    <div style={styles.wrapper}>
      <div className="App" style={{ ...styles.body, paddingTop: "50px" }}>
        <h1 style={{ ...styles.h1, textAlign: "left", fontSize: isMobile ? "1.5rem" : "2.5rem", marginBottom: "30px" }}>
          User Conversations
        </h1>

        {/* Statistics Cards */}
        {stats && (
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            marginBottom: '30px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              ...styles.responseContainer,
              flex: '1',
              minWidth: '200px',
              textAlign: 'center',
              marginTop: '0'
            }}>
              <h3 style={styles.responseContainerH3}>Total Customers</h3>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: styles.body.color }}>
                {stats.total_customers || 0}
              </div>
            </div>
            <div style={{
              ...styles.responseContainer,
              flex: '1',
              minWidth: '200px',
              textAlign: 'center',
              marginTop: '0'
            }}>
              <h3 style={styles.responseContainerH3}>Total Sessions</h3>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: styles.body.color }}>
                {stats.total_sessions || 0}
              </div>
            </div>
            <div style={{
              ...styles.responseContainer,
              flex: '1',
              minWidth: '200px',
              textAlign: 'center',
              marginTop: '0'
            }}>
              <h3 style={styles.responseContainerH3}>Total Messages</h3>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: styles.body.color }}>
                {stats.total_messages || 0}
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div style={{
          ...styles.responseContainer,
          marginTop: '0',
          marginBottom: '20px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
          alignItems: 'center'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            flex: isMobile ? '1 1 100%' : '1 1 auto',
            minWidth: '300px'
          }}>
            <input
              type="text"
              placeholder="Search conversations and messages..."
              value={searchInput}
              onChange={handleSearchInput}
              onKeyPress={handleSearchKeyPress}
              className={isDarkMode ? 'sleek-input-dark' : 'sleek-input-light'}
              style={{ 
                flex: '1',
                minWidth: '200px'
              }}
            />
            <button
              onClick={performSearch}
              disabled={!searchInput.trim()}
              style={{
                ...styles.btn,
                backgroundColor: !searchInput.trim() ? '#222222' : '#00d67a',
                color: '#fff',
                padding: '0 12px',
                borderRadius: '4px',
                minWidth: '80px',
                border: '1px solid transparent',
                fontSize: '14px',
                height: '38px',
                boxSizing: 'border-box',
                lineHeight: '36px',
                cursor: !searchInput.trim() ? 'not-allowed' : 'pointer'
              }}
              title="Search"
            >
              Search
            </button>
            {(searchQuery || searchInput) && (
              <button
                onClick={clearSearch}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: '5px',
                  color: isDarkMode ? '#999999' : '#6b7280',
                  borderRadius: '4px'
                }}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          
          {searchQuery && (
            <select
              value={searchType}
              onChange={handleSearchTypeChange}
              className={isDarkMode ? 'sleek-select-dark' : 'sleek-select-light'}
              style={{ minWidth: '120px' }}
              title="Search in"
            >
              <option value="all">All</option>
              <option value="messages">Messages</option>
              <option value="sessions">Sessions</option>
            </select>
          )}
          
          <select
            value={selectedFlow}
            onChange={handleFlowFilter}
            className={isDarkMode ? 'sleek-select-dark' : 'sleek-select-light'}
            style={{ minWidth: '150px' }}
          >
            <option value="">All Flows</option>
            {availableFlows.map(flow => (
              <option key={flow} value={flow}>{flow}</option>
            ))}
          </select>
          
          {!isSearchMode && (
            <select
              value={selectedCustomer}
              onChange={handleCustomerFilter}
              className={isDarkMode ? 'sleek-select-dark' : 'sleek-select-light'}
              style={{ minWidth: '200px' }}
            >
              <option value="">All Customers</option>
              {availableCustomers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.email} {customer.name ? `(${customer.name})` : ''}
                </option>
              ))}
            </select>
          )}
          
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(0);
            }}
            className={isDarkMode ? 'sleek-select-dark' : 'sleek-select-light'}
            style={{ minWidth: '100px' }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>

        {/* Search Mode Indicator */}
        {isSearchMode && searchQuery && (
          <div style={{
            ...styles.responseContainer,
            marginTop: '0',
            marginBottom: '20px',
            backgroundColor: isDarkMode ? 'rgba(0, 214, 122, 0.1)' : 'rgba(0, 214, 122, 0.1)',
            border: `1px solid ${isDarkMode ? '#00d67a' : '#00d67a'}`,
            padding: '12px 16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>
                Searching {searchType === 'all' ? 'conversations and messages' : searchType} for: 
                <strong style={{ marginLeft: '6px' }}>"{searchQuery}"</strong>
              </span>
              {selectedFlow && (
                <span style={{ marginLeft: '10px', opacity: 0.8 }}>
                  in flow: <strong>{selectedFlow}</strong>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            ...styles.responseContainer,
            backgroundColor: '#ff4444',
            color: '#fff',
            marginTop: '0',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {/* Loading Skeleton for better perceived performance */}
        {loading && (
          <div style={styles.responseContainer}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[...Array(3)].map((_, i) => (
                <div key={i} style={{
                  height: '60px',
                  backgroundColor: isDarkMode ? '#111111' : '#f3f4f6',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  opacity: 1 - (i * 0.2)
                }} />
              ))}
            </div>
          </div>
        )}

        {/* Sessions Table */}
        {!loading && (
          <div style={styles.responseContainer}>
            <div style={{ 
              overflowX: 'auto',
              marginTop: '0'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: isMobile ? '0.9rem' : '1rem'
              }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${isDarkMode ? '#1a1a1a' : '#e5e7eb'}` }}>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'left',
                      cursor: isSearchMode ? 'default' : 'pointer',
                      userSelect: 'none',
                      opacity: isSearchMode ? 0.7 : 1
                    }} onClick={isSearchMode ? undefined : () => handleSort('customer_email')}>
                      User {!isSearchMode && sortBy === 'customer_email' && (sortOrder === 'DESC' ? '↓' : '↑')}
                    </th>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'left',
                      cursor: isSearchMode ? 'default' : 'pointer',
                      userSelect: 'none',
                      opacity: isSearchMode ? 0.7 : 1
                    }} onClick={isSearchMode ? undefined : () => handleSort('flow_name')}>
                      Flow {!isSearchMode && sortBy === 'flow_name' && (sortOrder === 'DESC' ? '↓' : '↑')}
                    </th>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'left',
                      cursor: isSearchMode ? 'default' : 'pointer',
                      userSelect: 'none',
                      opacity: isSearchMode ? 0.7 : 1
                    }} onClick={isSearchMode ? undefined : () => handleSort('message_count')}>
                      Messages {!isSearchMode && sortBy === 'message_count' && (sortOrder === 'DESC' ? '↓' : '↑')}
                    </th>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'left',
                      cursor: isSearchMode ? 'default' : 'pointer',
                      userSelect: 'none',
                      opacity: isSearchMode ? 0.7 : 1
                    }} onClick={isSearchMode ? undefined : () => handleSort('last_message_at')}>
                      Last Activity {!isSearchMode && sortBy === 'last_message_at' && (sortOrder === 'DESC' ? '↓' : '↑')}
                    </th>
                    <th style={{ padding: '12px 8px', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session, index) => (
                    <tr key={session.session_id} style={{
                      borderBottom: `1px solid ${isDarkMode ? '#1a1a1a' : '#e5e7eb'}`,
                      backgroundColor: index % 2 === 0 ? 'transparent' : (isDarkMode ? '#0a0a0a' : '#ffffff')
                    }}>
                      <td style={{ padding: '12px 8px' }}>
                        <div style={{ fontWeight: 'bold' }}>
                          {session.session_name || 'Anonymous User'}
                        </div>
                        {session.customer_name && (
                          <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                            {session.customer_name}
                          </div>
                        )}
                        {isSearchMode && searchType === 'messages' && session.matching_messages && (
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#00d67a', 
                            marginTop: '4px',
                            fontWeight: '500'
                          }}>
                            {session.matching_messages.length} matching message{session.matching_messages.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        {session.flow_name || 'N/A'}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        {session.message_count || 0}
                        {isSearchMode && searchType === 'messages' && session.matching_messages && (
                          <div style={{ 
                            fontSize: '0.7rem', 
                            opacity: 0.7,
                            marginTop: '2px'
                          }}>
                            total
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        {formatDate(session.last_message_at)}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <button
                          onClick={() => handleSessionSelect(session)}
                          style={{
                            ...styles.btn,
                            backgroundColor: '#00d67a',
                            color: '#fff',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            marginRight: '8px'
                          }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteSession(session)}
                          style={{
                            ...styles.btn,
                            backgroundColor: '#ff4444',
                            color: '#fff',
                            padding: '6px 12px',
                            borderRadius: '4px'
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{
              marginTop: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div>
                {isSearchMode ? (
                  <>Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalSessions)} of {totalSessions} search results</>
                ) : (
                  <>Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalSessions)} of {totalSessions} conversations</>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 0}
                  style={{
                    ...styles.btn,
                    backgroundColor: currentPage === 0 ? '#222222' : '#00d67a',
                    color: '#fff',
                    padding: '6px 12px',
                    borderRadius: '4px'
                  }}
                >
                  Previous
                </button>
                <span style={{ padding: '6px 12px' }}>
                  Page {currentPage + 1} of {Math.ceil(totalSessions / pageSize)}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage >= Math.ceil(totalSessions / pageSize) - 1}
                  style={{
                    ...styles.btn,
                    backgroundColor: currentPage >= Math.ceil(totalSessions / pageSize) - 1 ? '#222222' : '#00d67a',
                    color: '#fff',
                    padding: '6px 12px',
                    borderRadius: '4px'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Message Modal */}
        {showMessageModal && selectedSession && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '1200px',
              height: '85vh',
              maxHeight: '800px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: isDarkMode ? '1px solid #1a1a1a' : '1px solid #e5e7eb'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '20px 24px',
                borderBottom: isDarkMode ? '1px solid #1a1a1a' : '1px solid #e5e7eb',
                backgroundColor: isDarkMode ? '#111111' : '#f3f4f6'
              }}>
                <div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '1.5rem', 
                    fontWeight: '600',
                    color: isDarkMode ? '#ffffff' : '#111827'
                  }}>
                    {isSearchMode ? 'Search Result - Conversation Details' : 'Conversation Details'}
                  </h3>
                  <div style={{ 
                    fontSize: '0.9rem', 
                    opacity: 0.8, 
                    marginTop: '4px',
                    color: isDarkMode ? '#999999' : '#6b7280'
                  }}>
                    {selectedSession.session_name || 'Anonymous User'} • {selectedSession.flow_name || 'No Flow'} • {formatDate(selectedSession.created_at)}
                    {isSearchMode && selectedSession.matching_messages && (
                      <span style={{ marginLeft: '10px', color: '#00d67a', fontWeight: '500' }}>
                        • {selectedSession.matching_messages.length} matching message{selectedSession.matching_messages.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <button 
                  onClick={() => setShowMessageModal(false)}
                  style={{ 
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '1.5rem', 
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    color: isDarkMode ? '#999999' : '#6b7280',
                    transition: 'all 0.2s',
                    ':hover': {
                      backgroundColor: isDarkMode ? '#111111' : '#f3f4f6'
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#111111' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{ 
                flex: 1,
                overflow: 'auto',
                padding: '24px',
                backgroundColor: isDarkMode ? '#0d0d0d' : '#ffffff'
              }}>
                {messagesLoading ? (
                  <div className="loading-spinner" style={{ display: 'block' }}>
                    <svg viewBox="0 0 50 50">
                      <circle cx="25" cy="25" r="20" stroke="#00d67a" strokeWidth="5" fill="none" />
                    </svg>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {sessionMessages.map((message, index) => {
                      // Parse metadata if it exists
                      let metadata = {};
                      try {
                        if (message.metadata && typeof message.metadata === 'string') {
                          metadata = JSON.parse(message.metadata);
                        } else if (message.metadata && typeof message.metadata === 'object') {
                          metadata = message.metadata;
                        }
                      } catch (e) {
                        console.warn('Failed to parse message metadata:', e);
                      }
                      
                      const messageId = message.message_uuid || `msg-${index}`;
                      const isMetadataExpanded = expandedMetadata.has(messageId);
                      const hasMetadata = metadata && Object.keys(metadata).length > 0;
                      
                      return (
                        <div key={messageId} style={{
                          padding: '16px',
                          borderRadius: '12px',
                        backgroundColor: message.message_type === 'user' 
                          ? (isDarkMode ? 'rgba(0, 214, 122, 0.1)' : 'rgba(0, 214, 122, 0.1)')
                          : (isDarkMode ? '#111111' : '#f3f4f6'),
                        alignSelf: message.message_type === 'user' ? 'flex-end' : 'flex-start',
                          maxWidth: '85%',
                          border: `1px solid ${isDarkMode ? '#222222' : '#d1d5db'}`,
                          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                        }}>
                          <div style={{ 
                            fontSize: '0.85rem', 
                            opacity: 0.8, 
                            marginBottom: '8px',
                            fontWeight: '600',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            <span>
                              {message.message_type === 'user' ? '👤 User' : '🤖 Assistant'}
                          {message.name && ` (${message.name})`}
                            </span>
                            <span style={{ fontSize: '0.75rem', opacity: 0.6, flexShrink: 0 }}>
                          {formatMessageTime(message.timestamp)}
                            </span>
                        </div>
                          
                          

                        <div style={{ 
                          whiteSpace: 'pre-wrap',
                            lineHeight: '1.5',
                            marginBottom: hasMetadata ? '12px' : '0'
                        }}>
                          {highlightSearchTerms(message.message, searchQuery)}
                        </div>

                          {/* Show key metadata inline for assistant messages */}
                          {message.message_type === 'assistant' && (metadata.model || metadata.response_time || metadata.vault_name) && (
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '6px',
                              marginTop: '8px',
                              marginBottom: hasMetadata ? '8px' : '0'
                            }}>
                              {metadata.model && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  padding: '2px 6px',
                                  backgroundColor: isDarkMode ? 'rgba(156, 163, 175, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                                  borderRadius: '3px',
                                  color: isDarkMode ? '#999999' : '#6b7280',
                                  fontWeight: '500'
                                }}>
                                  {metadata.model}
                                </span>
                              )}
                              {metadata.response_time && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  padding: '2px 6px',
                                  backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                                  borderRadius: '3px',
                                  color: isDarkMode ? '#00d67a' : '#00d67a',
                                  fontWeight: '500'
                                }}>
                                  {Math.round(metadata.response_time * 1000)}ms
                                </span>
                              )}
                              {metadata.vault_name && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  padding: '2px 6px',
                                  backgroundColor: isDarkMode ? 'rgba(168, 85, 247, 0.2)' : 'rgba(168, 85, 247, 0.2)',
                                  borderRadius: '3px',
                                  color: isDarkMode ? '#c4b5fd' : '#7c3aed',
                                  fontWeight: '500'
                                }}>
                                  {metadata.vault_name}
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Node type - hide respond and input as they're not helpful */}
                          {message.node_type && !['respond', 'input'].includes(message.node_type) && (
                            <div style={{ 
                              fontSize: '0.75rem', 
                              opacity: 0.7,
                              marginBottom: '8px',
                              padding: '4px 8px',
                              backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                              borderRadius: '4px',
                              display: 'inline-block',
                              color: '#3b82f6'
                            }}>
                              📍 Node: {message.node_type}
                            </div>
                          )}
                          
                          {/* Enhanced metadata display - clickable to expand/collapse */}
                          {hasMetadata && (
                            <div style={{
                              marginTop: '12px',
                              backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
                              borderRadius: '8px',
                              border: `1px solid ${isDarkMode ? '#1a1a1a' : '#e5e7eb'}`
                            }}>
                              <div 
                                onClick={() => toggleMetadata(messageId)}
                                style={{ 
                                  padding: '8px 12px',
                                  fontWeight: '500', 
                                  color: isDarkMode ? '#999999' : '#6b7280',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  fontSize: '0.7rem',
                                  userSelect: 'none',
                                  opacity: 0.7,
                                  transition: 'all 0.2s ease',
                                  borderRadius: '4px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
                                  e.target.style.opacity = '1';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.opacity = '0.7';
                                }}
                              >
                                <span>Message Details</span>
                                <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>
                                  {isMetadataExpanded ? '▼' : '▶'}
                                </span>
                              </div>
                              
                              {isMetadataExpanded && (
                                <div style={{ 
                                  padding: '0 12px 12px 12px',
                                  fontSize: '0.75rem'
                                }}>
                                  <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                                    gap: '8px' 
                                  }}>
                                    {metadata.vault_name && (
                                      <div><strong>Vault:</strong> {metadata.vault_name}</div>
                                    )}
                                    {metadata.invoke_method && (
                                      <div><strong>Invoke Method:</strong> {metadata.invoke_method}</div>
                                    )}
                                    {metadata.model && (
                                      <div><strong>Model:</strong> {metadata.model}</div>
                                    )}
                                    {metadata.current_node_id && (
                                      <div><strong>Node ID:</strong> {metadata.current_node_id}</div>
                                    )}
                                    {metadata.temperature !== undefined && (
                                      <div><strong>Temperature:</strong> {metadata.temperature}</div>
                                    )}
                                    {metadata.response_time && (
                                      <div><strong>Response Time:</strong> {Math.round(metadata.response_time * 1000)}ms</div>
                                    )}
                                    {metadata.n_context && (
                                      <div><strong>Context Items:</strong> {metadata.n_context}</div>
                                    )}
                                    {metadata.get_context !== undefined && (
                                      <div><strong>Get Context:</strong> {metadata.get_context ? 'Yes' : 'No'}</div>
                                    )}
                                    {metadata.smart_history_search !== undefined && (
                                      <div><strong>Smart History:</strong> {metadata.smart_history_search ? 'Yes' : 'No'}</div>
                                    )}
                                    {metadata.static_response !== undefined && (
                                      <div><strong>Static Response:</strong> {metadata.static_response ? 'Yes' : 'No'}</div>
                                    )}
                                    {metadata.timeout && (
                                      <div><strong>Timeout:</strong> {metadata.timeout}s</div>
                                    )}
                                  </div>
                                  
                                  {/* Show system prompt if available */}
                                  {metadata.prompt && (
                                    <div style={{ marginTop: '12px' }}>
                                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                                        System Prompt:
                                      </div>
                          <div style={{ 
                                        fontFamily: 'monospace',
                                        backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)',
                                        padding: '8px',
                                        borderRadius: '4px',
                                        whiteSpace: 'pre-wrap',
                            fontSize: '0.7rem', 
                                        maxHeight: '200px',
                                        overflow: 'auto',
                                        border: `1px solid ${isDarkMode ? '#222222' : '#d1d5db'}`
                                      }}>
                                        {metadata.prompt}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                    {sessionMessages.length === 0 && (
                      <div style={{ textAlign: 'center', opacity: 0.7, padding: '40px' }}>
                        No messages found in this conversation.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationManager; 